import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Business, ActivityForHomepage } from '@/types/homepage';
import { fetchBusinessesInBounds, fetchBusinessActivities } from '@/utils/homepageApi';
import { log } from '@/utils/logger';

/**
 * Business Store - Domain-focused state management for business data
 * 
 * Responsibilities:
 * - Business data management
 * - Business activities
 * - Business selection
 * - Business filtering results
 * 
 * Following industry best practices:
 * - Single Responsibility Principle
 * - Immutable state updates
 * - Separation of concerns
 * - Type safety
 */

// ===== INTERFACES =====

interface BusinessState {
  // Core business data
  businesses: Business[];
  filteredBusinesses: Business[];
  selectedBusiness: Business | null;
  
  // Business activities
  businessActivities: Record<string, ActivityForHomepage[]>;
  
  // Loading states (business-specific only)
  isLoadingBusinesses: boolean;
  isLoadingActivities: boolean;
  
  // Error states
  businessError: string | null;
  activitiesError: string | null;
}

interface BusinessActions {
  // Business data operations
  setBusinesses: (businesses: Business[]) => void;
  setFilteredBusinesses: (businesses: Business[]) => void;
  updateBusinessData: (businesses: Business[], filtered: Business[]) => void;
  
  // Business selection
  selectBusiness: (business: Business | null) => void;
  
  // Business activities
  loadBusinessActivities: (businessId: string) => Promise<void>;
  setBusinessActivities: (businessId: string, activities: ActivityForHomepage[]) => void;
  
  // State management
  setBusinessLoading: (isLoading: boolean) => void;
  setActivitiesLoading: (isLoading: boolean) => void;
  setBusinessError: (error: string | null) => void;
  setActivitiesError: (error: string | null) => void;
  
  // Utility functions
  clearBusinessData: () => void;
  getBusinessById: (id: string) => Business | undefined;
}

type BusinessStore = BusinessState & BusinessActions;

// ===== INITIAL STATE =====

const initialState: BusinessState = {
  businesses: [],
  filteredBusinesses: [],
  selectedBusiness: null,
  businessActivities: {},
  isLoadingBusinesses: false,
  isLoadingActivities: false,
  businessError: null,
  activitiesError: null,
};

// ===== STORE IMPLEMENTATION =====

export const useBusinessStore = create<BusinessStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ===== BUSINESS DATA OPERATIONS =====
      
      setBusinesses: (businesses: Business[]) => {
        set({ businesses }, false, 'business/setBusinesses');
      },

      setFilteredBusinesses: (filteredBusinesses: Business[]) => {
        set({ filteredBusinesses }, false, 'business/setFilteredBusinesses');
      },

      updateBusinessData: (businesses: Business[], filtered: Business[]) => {
        set({ 
          businesses, 
          filteredBusinesses: filtered,
          businessError: null 
        }, false, 'business/updateBusinessData');
      },

      // ===== BUSINESS SELECTION =====
      
      selectBusiness: (business: Business | null) => {
        const currentSelected = get().selectedBusiness;
        
        // Only update if actually changing
        if (currentSelected?._id !== business?._id) {
          set({ selectedBusiness: business }, false, 'business/selectBusiness');
          
          // Preload activities for selected business
          if (business?._id) {
            const { loadBusinessActivities } = get();
            loadBusinessActivities(business._id);
          }
        }
      },

      // ===== BUSINESS ACTIVITIES =====
      
      loadBusinessActivities: async (businessId: string) => {
        const { businessActivities, isLoadingActivities } = get();
        
        // Skip if already loaded or loading
        if (businessActivities[businessId] || isLoadingActivities) {
          return;
        }

        set({ isLoadingActivities: true, activitiesError: null }, false, 'business/loadActivities/start');

        try {
          const activities = await fetchBusinessActivities(businessId);
          
          set(state => ({
            businessActivities: {
              ...state.businessActivities,
              [businessId]: activities
            },
            isLoadingActivities: false
          }), false, 'business/loadActivities/success');

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load activities';
          log.error('[BusinessStore] Failed to load activities:', { businessId, error });
          
          set({ 
            isLoadingActivities: false, 
            activitiesError: errorMessage 
          }, false, 'business/loadActivities/error');
        }
      },

      setBusinessActivities: (businessId: string, activities: ActivityForHomepage[]) => {
        set(state => ({
          businessActivities: {
            ...state.businessActivities,
            [businessId]: activities
          }
        }), false, 'business/setBusinessActivities');
      },

      // ===== STATE MANAGEMENT =====
      
      setBusinessLoading: (isLoading: boolean) => {
        set({ isLoadingBusinesses: isLoading }, false, 'business/setBusinessLoading');
      },

      setActivitiesLoading: (isLoading: boolean) => {
        set({ isLoadingActivities: isLoading }, false, 'business/setActivitiesLoading');
      },

      setBusinessError: (error: string | null) => {
        set({ businessError: error }, false, 'business/setBusinessError');
      },

      setActivitiesError: (error: string | null) => {
        set({ activitiesError: error }, false, 'business/setActivitiesError');
      },

      // ===== UTILITY FUNCTIONS =====
      
      clearBusinessData: () => {
        set({
          businesses: [],
          filteredBusinesses: [],
          selectedBusiness: null,
          businessActivities: {},
          businessError: null,
          activitiesError: null
        }, false, 'business/clearBusinessData');
      },

      getBusinessById: (id: string) => {
        return get().businesses.find(business => business._id === id);
      },
    }),
    {
      name: 'business-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ===== SELECTOR HOOKS =====

/**
 * Optimized selectors for common use cases
 * These prevent unnecessary re-renders by only subscribing to specific data
 */

// Only re-render when filtered businesses change
export const useFilteredBusinesses = () => 
  useBusinessStore(state => state.filteredBusinesses);

// Only re-render when selected business changes
export const useSelectedBusiness = () => 
  useBusinessStore(state => state.selectedBusiness);

// Only re-render when business activities change
export const useBusinessActivities = () => 
  useBusinessStore(state => state.businessActivities);

// Only re-render when loading states change (memoized to prevent infinite loops)
export const useBusinessLoading = () => {
  const isLoadingBusinesses = useBusinessStore(state => state.isLoadingBusinesses);
  const isLoadingActivities = useBusinessStore(state => state.isLoadingActivities);
  
  return useMemo(() => ({
    isLoadingBusinesses,
    isLoadingActivities
  }), [isLoadingBusinesses, isLoadingActivities]);
};

// ===== TYPE EXPORTS =====

export type { BusinessState, BusinessActions, BusinessStore }; 